import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  HeartPulse,
  Copy,
  Check,
  ExternalLink,
  Play,
  Activity,
  FolderOpen,
  AlertTriangle,
  Layers,
  ScanLine,
  Brain,
  Package,
  BarChart3,
  Database,
  Plus,
  CheckCircle2,
  Clock,
  ArrowRight,
  Cpu,
  MemoryStick,
} from 'lucide-react';
import {
  MONAI15InstanceDetails,
  MONAIExperiment,
  MONAITaskType,
  mockMONAI15InstanceDetails,
} from '@/mock/gpu-instance';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import PATH_PAGE from '@/router/path';

interface MONAIDetailPanelProps {
  taskId: string;
}

const getTaskIcon = (task: MONAITaskType) => {
  switch (task) {
    case 'segmentation':
      return <Layers className="h-4 w-4 text-purple-500" />;
    case 'classification':
      return <ScanLine className="h-4 w-4 text-blue-500" />;
    case 'detection':
      return <Brain className="h-4 w-4 text-green-500" />;
    case 'registration':
      return <Brain className="h-4 w-4 text-orange-500" />;
  }
};

const getStatusBadge = (status: MONAIExperiment['status']) => {
  switch (status) {
    case 'training':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Play className="h-3 w-3 mr-1" /> Training
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          Failed
        </Badge>
      );
    case 'queued':
      return (
        <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
          <Clock className="h-3 w-3 mr-1" /> Queued
        </Badge>
      );
    case 'evaluating':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <BarChart3 className="h-3 w-3 mr-1" /> Evaluating
        </Badge>
      );
  }
};

const MONAIDetailPanel = ({ taskId }: MONAIDetailPanelProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { instanceId } = useParams<{ instanceId: string }>();
  const { withDemoPrefix } = useDemoPath();
  const navigate = useNavigate();

  const details: MONAI15InstanceDetails = useMemo(
    () => mockMONAI15InstanceDetails(taskId),
    [taskId]
  );

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trainingExperiments = details.experiments.filter(
    (e) => e.status === 'training'
  ).length;
  const completedExperiments = details.experiments.filter(
    (e) => e.status === 'completed'
  ).length;

  const navigateToExperiments = () => {
    if (instanceId) {
      navigate(
        withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiExperiments)
      );
    }
  };

  const navigateToDatasets = () => {
    if (instanceId) {
      navigate(
        withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiDatasets)
      );
    }
  };

  const navigateToBundles = () => {
    if (instanceId) {
      navigate(
        withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, instanceId).monaiModels)
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-red-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <HeartPulse className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  NVIDIA MONAI
                  <Badge
                    variant="outline"
                    className={
                      details.status === 'active'
                        ? 'bg-green-500/10 text-green-600 border-green-500/30'
                        : 'bg-gray-500/10 text-gray-600'
                    }
                  >
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Medical AI framework for healthcare imaging - Version {details.monaiVersion}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono">
                v{details.monaiVersion}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(details.notebookUrl, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Jupyter
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* GPU Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
              <Cpu className="h-4 w-4" />
              GPU Util
            </div>
            <div className="text-2xl font-bold text-green-600">
              {details.gpuUtilization}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
              <MemoryStick className="h-4 w-4" />
              VRAM
            </div>
            <div className="text-2xl font-bold">
              {details.gpuMemoryUsed}/{details.gpuMemoryTotal} GB
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-sm text-muted-foreground mb-1">Training</div>
            <div className="text-2xl font-bold text-blue-600">
              {trainingExperiments}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-sm text-muted-foreground mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-600">
              {completedExperiments}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-4">
        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={navigateToExperiments}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="font-medium">Experiments</div>
                  <div className="text-sm text-muted-foreground">
                    {details.experiments.length} total
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={navigateToDatasets}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Database className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <div className="font-medium">Datasets</div>
                  <div className="text-sm text-muted-foreground">
                    {details.datasets.length} available
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={navigateToBundles}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Package className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <div className="font-medium">Model Bundles</div>
                  <div className="text-sm text-muted-foreground">
                    {details.bundles.length} bundles
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Experiments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Experiments</CardTitle>
              <CardDescription>Latest training experiments</CardDescription>
            </div>
            <Button size="sm" onClick={navigateToExperiments}>
              <Plus className="h-4 w-4 mr-1" />
              New Experiment
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {details.experiments.slice(0, 3).map((experiment) => (
            <Card key={experiment.id} className="bg-muted/30">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getTaskIcon(experiment.task)}
                    <div>
                      <div className="font-medium">{experiment.name}</div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span>{experiment.bundle.displayName}</span>
                        <span>|</span>
                        <span>{experiment.dataset.displayName}</span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(experiment.status)}
                </div>

                {experiment.status === 'training' && (
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        Epoch {experiment.metrics.currentEpoch}/
                        {experiment.metrics.totalEpochs} ({experiment.progress}%)
                      </span>
                    </div>
                    <Progress value={experiment.progress} className="h-2" />
                  </div>
                )}

                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="p-2 bg-background rounded text-center">
                    <div className="text-muted-foreground text-xs">Loss</div>
                    <div className="font-mono font-medium">
                      {experiment.metrics.trainLoss.toFixed(4)}
                    </div>
                  </div>
                  {experiment.metrics.diceScore !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">Dice</div>
                      <div className="font-mono font-medium text-green-600">
                        {(experiment.metrics.diceScore * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {experiment.metrics.accuracy !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">Acc</div>
                      <div className="font-mono font-medium text-green-600">
                        {(experiment.metrics.accuracy * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {experiment.metrics.auc !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">AUC</div>
                      <div className="font-mono font-medium text-green-600">
                        {experiment.metrics.auc.toFixed(3)}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {details.experiments.length > 3 && (
            <Button
              variant="outline"
              className="w-full"
              onClick={navigateToExperiments}
            >
              View All {details.experiments.length} Experiments
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Workspace Paths */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Workspace Paths
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(details.workspacePaths).map(([key, path]) => (
            <div key={key} className="space-y-1">
              <Label className="text-sm text-muted-foreground capitalize">
                {key}
              </Label>
              <div className="relative">
                <pre className="bg-muted p-2 rounded-lg text-sm font-mono pr-12">
                  {path}
                </pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-1 right-1 h-6"
                  onClick={() => handleCopy(path, key)}
                >
                  {copiedField === key ? (
                    <Check className="size-3" />
                  ) : (
                    <Copy className="size-3" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data.
          In production, MONAI experiments and metrics will update in real-time.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MONAIDetailPanel;
